/**
 * 에이전트가 내리는 **쓰기 초안(`action` 이벤트)의 카탈로그** (docs/18 §5.4).
 *
 * ## 왜 카탈로그인가
 *
 * `AiAgentPage` 가 `else if (event.kind === 'submit_issuance')` 한 줄로 처리하고 있었다.
 * 쓰기 기능이 늘 때마다 그 자리에 분기가 하나씩 붙고, 어떤 kind 가 처리되는지는 코드를 읽어야만
 * 알 수 있다. **모르는 kind 가 오면 조용히 아무 일도 일어나지 않는다** — 에이전트는 초안을
 * 내렸는데 화면에는 카드가 없고, 사용자는 자기 요청이 사라진 줄 안다.
 *
 * 여기 한 줄이 그 분기를 대신한다. 모르는 kind 는 `resolveAction` 이 `null` 을 돌려주고,
 * 화면은 **그 사실을 오류로 드러낸다**(조용히 넘어가지 않는다).
 *
 * ## 확인은 대화가 한다 (사용자 지시 2026-09-11)
 *
 * 예전에는 요약 표가 붙은 **확인 카드**가 떴고, 그 카드를 건너뛰는 「확인 없이 실행」 스위치가
 * 제목 줄에 따로 있었다. 둘 다 없앴다 — 에이전트가 **문장으로 값을 말하고 「지금 …해 드릴까요?」
 * 라고 묻는다.** 사용자는 「네」를 누르거나 그냥 그렇게 적으면 되고, 그 승낙이 대화 이력에 남는다.
 * 그래서 이 카탈로그에는 이제 `summary`(요약 표)가 없다 — 값을 말하는 쪽은 에이전트의 문장이다.
 *
 * ## 실행은 언제나 프론트가 세션 인증으로 한다
 *
 * 에이전트는 payload 를 만들어 줄 뿐이고 backend 를 부르지 않는다. 위임 토큰은 GET 전용이라
 * (`DelegationScope`) 애초에 쓰기가 막혀 있고, 그래야 감사 로그의 "누가" 가 사람으로 남는다.
 * **`submit` 안에서 `apiFetch` 를 쓰는 것이 그 계약의 실체다** — 쿠키 세션으로 나간다.
 *
 * ## agent 쪽과 짝이다
 *
 * `agent/app/agents/registry.py` 의 `Domain.actions` 가 "에이전트가 낼 수 있는 kind" 이고
 * 여기가 "화면이 받을 수 있는 kind" 다. 한쪽만 늘리면 낸 초안이 그려지지 않거나, 그리지도 못할
 * 카드를 준비해 두게 된다. `agent/tests/test_actions_sync.py` 가 두 파일을 읽어 대조한다.
 */

import { apiFetch } from '@/lib/api/client';
import { easyModeOn, setEasyMode } from '@/lib/easy/useEasyMode';
import { queryKeys } from '@/lib/queryKeys';

export type ActionResult = {
  message: string;
  links?: { label: string; href: string }[];
  /**
   * **대화 이력에 남길 한 줄.** 없으면 `message` 를 쓴다.
   *
   * ## 왜 남기나
   *
   * 예전에는 확인 버튼을 누른 결과가 카드의 지역 상태에만 남았다. 그래서 다음 발화를 보내면
   * 카드가 사라지고, **에이전트는 자기가 내민 초안이 실행됐는지 몰랐다** — 「방금 그거 어떻게
   * 됐어」에 "무엇을 말씀하시는지 모르겠습니다" 로 답하던 자리다. 실행 사실을 이력 한 줄로
   * 남기면 다음 턴의 `history` 에 저절로 실린다(에이전트 코드는 한 줄도 안 바뀐다).
   *
   * ## 카드 문구와 왜 갈리나
   *
   * `message` 는 **방금 승낙한 사람**에게 말하고("등록했습니다"), 이력은 **다음 턴의 모델**에게
   * 말한다("완주 물류센터 · 공정율 87.3% 를 등록했습니다"). 뒤엣것이 짧으면 무엇이 실행됐는지
   * 모델이 읽지 못해, 이어지는 물음에 또 헤맨다.
   */
  receipt?: string;
};

export type ActionSpec = {
  kind: string;
  /**
   * 승낙 버튼 문구 — <b>사용자가 하는 대답</b>이다(사용자 지시 2026-09-11).
   *
   * <p>"확인하고 등록" 이 아니라 "네, 등록해 주세요" 로 적는다. 누르면 그 말이 사용자 말풍선으로
   * 이력에 남고, 손으로 「네」라고 적어도 같은 자리로 들어온다({@link AgentChatProvider} §승낙).
   * 무엇이 일어나는지는 동사가 말한다 — "네" 만으로는 알 수 없다.
   */
  confirmLabel: string;
  /**
   * 실제 호출. **세션 인증(`apiFetch`)으로 나간다** — 위임 토큰이 끼지 않는 것이 이 계약의 실체다.
   * `custom` 인 kind 는 전용 카드가 자기 방식으로 부르므로 비워 둔다.
   */
  submit?: (payload: Record<string, unknown>) => Promise<ActionResult>;
  /**
   * 성공한 뒤 내려야 할 캐시 키. **쓰기가 바꾼 화면이 대화 밖에 있을 때** 채운다 —
   * 이 앱은 한 번 뜬 뒤 페이지를 새로 받지 않으므로, 내리지 않으면 사용자는 "바꿨습니다" 를 보고
   * 그 화면에 가서 옛 값을 만난다.
   *
   * 카탈로그가 들고 있는 이유는 **무엇이 낡는지 아는 쪽이 여기**이기 때문이다 —
   * {@link AgentChatProvider} 는 kind 를 모른 채 부르기만 한다.
   */
  invalidates?: readonly (readonly unknown[])[];
  /**
   * 전용 카드가 따로 있는 kind. 발급이 그렇다 — PDF 미리보기·워터마크·결재 여부별 안내가
   * 문장으로 표현되지 않는다({@link IssuancePreviewCard}).
   */
  custom?: true;
};

const text = (payload: Record<string, unknown>, key: string): string | null => {
  const value = payload[key];
  return value === null || value === undefined || value === '' ? null : String(value);
};

/** 초안에 실려 온 자료실 파일들. 모양이 어긋난 줄은 버린다 — 절반만 든 값으로 서버를 부르지 않는다. */
const attachedDocs = (payload: Record<string, unknown>): { source: string; ref: string; name: string }[] => {
  const rows = payload.docs;
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => (row && typeof row === 'object' ? row as Record<string, unknown> : {}))
    .filter((row) => typeof row.ref === 'string' && row.ref)
    .map((row) => ({
      source: typeof row.source === 'string' ? row.source : 'S3',
      ref: String(row.ref),
      name: typeof row.name === 'string' && row.name ? row.name : String(row.ref).split('/').pop() ?? '',
    }));
};

export const ACTIONS: Record<string, ActionSpec> = {
  /**
   * <b>붙인 자료실 파일을 지금 보고 있는 서류 자리에 건다</b> (사용자 지시 2026-09-04).
   *
   * ## 올리지 않는다 — 가리킨다
   *
   * 파일은 저장소에 그대로 있고, 이 현장·이 분류의 자료라는 <b>뜻만</b> 붙인다
   * (`POST /documents/files/attach`). 같은 계약서가 두 벌로 앉지 않는다 — 탭의
   * 「이미 올린 파일 고르기」가 쓰는 바로 그 통로다.
   *
   * ## 코드 → id 는 <b>여기서</b> 푼다
   *
   * 서버는 분류 id(UUID)를 요구하고 「짐작하지 않는다」고 못 박혀 있다({@code DocumentAttachRequest}).
   * 에이전트는 UUID 를 다루지 않는 것이 규율이라(현장 이름과 같다) 코드·이름으로 받아
   * 이 자리에서 푼다. 못 찾으면 <b>걸지 않고</b> 그렇게 말한다.
   */
  attach_document_to_slot: {
    kind: 'attach_document_to_slot',
    confirmLabel: '네, 걸어 주세요',
    // 서류 자리의 목록 캐시. 안 내리면 사용자는 "걸었습니다" 를 보고 그 탭에서 옛 목록을 만난다
    // (`useDocuments` 가 이 접두어로 걸어 두고, 첨부 훅도 같은 키를 내린다).
    invalidates: [['document-files']],
    submit: async (p) => {
      const docs = attachedDocs(p);
      const code = text(p, 'categoryCode');
      if (docs.length === 0 || !code) {
        // 재료가 모자란 초안이 왔다. **부르지 않는다** — 분류 없이 보내면 서버가 400 을 내고,
        // 사용자에게는 "실패" 만 남는다.
        return { message: '어느 자리에 걸지 정해지지 않았습니다. 탭을 열어 둔 채 다시 말씀해 주세요.' };
      }

      const categories = await apiFetch<{ id: string; code: string }[]>('/v1/documents/categories');
      const category = categories.find((row) => row.code === code);
      if (!category) {
        return { message: `자료실에 '${code}' 분류가 없습니다. 자료실 › 문서 분류에서 확인해 주세요.` };
      }

      // 현장은 <b>이름으로</b> 온다(에이전트 규율). 여기서 id 로 바꾼다 — 못 찾으면 본사/공용이
      // 아니라 **모름**이므로 걸지 않는다.
      const siteName = text(p, 'siteName');
      let siteId: string | null = null;
      if (siteName) {
        const sites = await apiFetch<{ id: string; name: string }[]>('/v1/sites');
        siteId = sites.find((row) => row.name === siteName)?.id ?? null;
        if (!siteId) {
          return { message: `'${siteName}' 현장을 찾지 못했습니다.` };
        }
      }

      for (const doc of docs) {
        await apiFetch('/v1/documents/files/attach', {
          method: 'POST',
          body: {
            source: doc.source,
            ref: doc.ref,
            connectionId: null,
            categoryId: category.id,
            siteId,
            name: doc.name,
          },
        });
      }

      const where = [siteName, text(p, 'slotLabel')].filter(Boolean).join(' · ');
      return {
        message: `${docs.length}건을 ${where} 자리에 걸었습니다.`,
        receipt: ['[실행됨] 자료 걸기', where, docs.map((d) => d.name).join(' · ')]
          .filter(Boolean).join(' · '),
      };
    },
  },

  /**
   * 증명서 발급 신청. 전용 카드가 미리보기 PDF 까지 그린다.
   *
   * 여기서 `submit` 을 채우지 않는 이유: 발급은 **미리보기와 같은 입력으로 신청**해야 하고
   * (눈으로 확인한 그 문서가 그대로 나가야 한다), 그 짝을 맞추는 일이 카드 안에 있다.
   */
  submit_issuance: {
    kind: 'submit_issuance',
    confirmLabel: '네, 신청해 주세요',
    custom: true,
  },

  /**
   * 주간회의 현장 업무 등록 — **문장으로 묻는 첫 사례** (DOC-20 §1.13).
   *
   * 발급과 달리 전용 카드가 필요 없다. 확인할 것이 네 줄(주차·현장·분류·내용)이고 미리보기랄
   * 것이 없다 — 저장될 값이 곧 에이전트가 말한 값이다.
   *
   * ## 순서를 지켜 열었다
   *
   * `POST /v1/site-tasks` 는 **계층2가 없었다.** 현장 A 담당자가 현장 B 의 공정율 줄을 고칠 수
   * 있었고, 그 값은 현장 정보이 그대로 그린다. 그래서 backend `SiteTaskService` 에
   * `SiteScopeGuard` 를 먼저 걸고(DOC-20 §1.12) 그다음 이 줄을 넣었다.
   *
   *     인가 조사 → backend 확인/보강 → 에이전트 action → 이 카탈로그
   */
  create_site_task: {
    kind: 'create_site_task',
    confirmLabel: '네, 등록해 주세요',
    submit: async (p) => {
      await apiFetch('/v1/site-tasks', {
        method: 'POST',
        body: {
          weekId: p.weekId,
          siteId: p.siteId,
          category: p.category,
          content: p.content,
          departmentId: null,
          scheduleDate: null,
        },
      });
      return {
        message: '주간회의 현장 업무에 등록했습니다.',
        receipt: [
          '[실행됨] 주간회의 현장 업무 등록',
          text(p, 'weekLabel'), text(p, 'siteName'), text(p, 'category'),
          text(p, 'display') ?? text(p, 'content'),
        ].filter(Boolean).join(' · '),
        links: [{ label: '주간업무 열기', href: '/meetings/weekly' }],
      };
    },
  },

  /**
   * AI 인사말 켬/끔 (DOC-66 §8.1 · 사용자 지시 2026-08-27).
   *
   * ## 인가를 따로 조사할 것이 없던 첫 사례
   *
   * `PUT /v1/home/greeting/preference` 는 **경로에도 본문에도 사람이 없다.** 컨트롤러가
   * 로그인 사용자 id 로만 쓰고, 남을 가리키는 방법 자체가 없다 — 현장 업무 등록에서 계층2를
   * 먼저 걸어야 했던 것(`create_site_task`)과 갈리는 지점이다.
   *
   * ## 캐시를 함께 내린다
   *
   * 켜고 끈 결과는 **홈 제목 줄**에 나타나는데, 그 조회는 캐시가 길다(`expiresAt` 까지).
   * 내리지 않으면 사용자는 "껐습니다" 를 보고 홈에 가서 인사말을 다시 만난다.
   */
  set_greeting: {
    kind: 'set_greeting',
    // 켜기인지 끄기인지는 **에이전트의 문장**이 말한다 — 버튼 문구는 kind 하나에 하나뿐이다.
    confirmLabel: '네, 바꿔 주세요',
    invalidates: [queryKeys.homeGreetingPreference, queryKeys.homeGreeting],
    submit: async (p) => {
      await apiFetch('/v1/home/greeting/preference', {
        method: 'PUT',
        body: { enabled: !!p.enabled },
      });
      return {
        message: p.enabled
          ? 'AI 인사말을 켰습니다. 홈 화면 제목 줄에 오늘의 한 마디가 함께 표시됩니다.'
          : 'AI 인사말을 껐습니다. 홈 화면에는 예전처럼 「○○ 님, 안녕하세요」 만 표시됩니다.',
        receipt: `[실행됨] AI 인사말 ${p.enabled ? '켜기' : '끄기'}`,
        links: [{ label: '홈 열기', href: '/home' }],
      };
    },
  },

  /**
   * 전자결재 기안 작성 + 상신 (사용자 지시 2026-09-01).
   *
   * ## 결재선을 화면이 만들지 않는다
   *
   * `lines` 는 에이전트가 `GET /approvals/line-preview` 에서 받아 온 것, 즉 **전결규정이 세운
   * 결재선 그대로**다. 모델도 이 화면도 결재자를 고르지 않는다 — 기안 화면이 쓰는 것과 같은 통로,
   * 같은 값이다.
   *
   * ## 두 번 부른다
   *
   * `POST /v1/approvals`(임시저장) → `POST /v1/approvals/{id}/submit`(상신). backend 가 그렇게
   * 갈라 두었고, 화면도 같은 순서로 부른다. **저장까지 됐는데 상신에서 실패하면 임시저장으로
   * 남는다** — 그 사실을 결과 문장에 적는다. 조용히 "실패했습니다" 로 끝내면 사용자는 문서가
   * 없는 줄 알고 같은 기안을 하나 더 만든다.
   *
   * ## 되돌릴 수 있다
   *
   * 상신한 기안은 **첫 결재 전까지 회수**된다(`POST /{id}/recall` · 전자결재 화면의 「회수」).
   * 증명서 발급이 인사팀에 도달하면 못 되돌리는 것과 갈리는 지점이고, 그래서 이 kind 는
   * 승낙 한 번으로 상신까지 간다.
   */
  submit_approval: {
    kind: 'submit_approval',
    confirmLabel: '네, 상신해 주세요',
    invalidates: [queryKeys.aiAgentSessions],
    submit: async (p) => {
      const created = await apiFetch<{ id: string; docNo?: string }>('/v1/approvals', {
        method: 'POST',
        body: {
          formKey: p.formKey,
          title: p.title,
          body: p.body || null,
          amount: p.amount ?? null,
          lines: p.lines,
          // 서식별 정형 항목. 키는 `formSchema.ts` 와 같아야 하고, 그 계약은 에이전트 쪽
          // `tests/test_form_fields_sync.py` 가 두 파일을 대조해 지킨다 — 어긋나면 backend 는
          // 받아서 저장하고 **기안 화면의 칸만 빈 채로** 뜬다.
          formData: p.formData ?? {},
        },
      });

      try {
        await apiFetch(`/v1/approvals/${created.id}/submit`, { method: 'POST' });
      } catch (e) {
        // 저장은 됐다. 그 사실을 감추지 않는다 — 감추면 같은 기안이 하나 더 생긴다.
        const reason = e instanceof Error ? e.message : '알 수 없는 오류';
        throw new Error(`기안은 임시저장됐지만 상신하지 못했습니다 (${reason}). 전자결재 > 임시저장에서 이어서 올려 주세요.`);
      }

      return {
        message: '기안을 상신했습니다. 첫 결재 전까지는 전자결재 화면에서 회수할 수 있습니다.',
        receipt: [
          '[실행됨] 전자결재 기안 상신',
          text(p, 'formName'), text(p, 'title'),
          p.amount == null ? null : `${Number(p.amount).toLocaleString()}원`,
          text(p, 'lineDisplay') ? `결재선 ${text(p, 'lineDisplay')}` : null,
        ].filter(Boolean).join(' · '),
        links: [{ label: '상신함 열기', href: '/approval' }],
      };
    },
  },

  /**
   * 내 할 일 추가 (2026-09-01).
   *
   * ## 인가를 따로 조사할 것이 없던 두 번째 사례
   *
   * `POST /v1/todos` 는 컨트롤러가 {@code MenuGuard} 를 쓰지 않는다 — 메뉴 밖의 개인 자료라
   * 로그인만 확인하고, 서비스가 세션 사용자 것만 만든다({@code todoService.add(userId(user), …)}).
   * 경로에도 본문에도 사람을 가리키는 값이 없다. `set_greeting` 과 같은 모양이다.
   *
   * <p>남에게 배정하는 통로({@code TodoAssignmentController})는 열지 않았다 — 그쪽은 본문에
   * 대상자가 실린다.
   */
  create_todo: {
    kind: 'create_todo',
    confirmLabel: '네, 추가해 주세요',
    invalidates: [queryKeys.todoItems],
    submit: async (p) => {
      await apiFetch('/v1/todos', {
        method: 'POST',
        body: { content: p.content, dueDate: p.dueDate ?? null },
      });
      return {
        message: '할 일에 추가했습니다.',
        receipt: ['[실행됨] 할 일 추가', text(p, 'content'), text(p, 'dueDate') && `기한 ${text(p, 'dueDate')}`]
          .filter(Boolean).join(' · '),
        links: [{ label: '홈 열기', href: '/home' }],
      };
    },
  },

  /**
   * 개선요청 등록 (2026-09-01).
   *
   * ## 어느 표로 가는가 — 요청게시판이다 (사용자 결정 2026-09-01)
   *
   * 코드에는 「개선요청」이 둘이다. 헤더 빨간 버튼({@link ImprovementRequestModal})은
   * `improvement_requests` 에 쌓이고, 사이드바 「개선요청」 화면은 `request_board_posts` 를 읽는다.
   * 사용자에게는 <b>하나</b>이고, 「어디서 작성하든 요청게시판으로 들어가야 한다」는 것이 결정이다.
   *
   * <p>그래서 대화는 `POST /v1/request-posts` 로 보낸다 — 담당자가 반영여부·소요기간을 답하는
   * 그 목록이다. <b>헤더 모달·홈 인사말도 같은 곳으로 들어간다</b>(V185, 2026-09-01) — 옛 접수
   * 창구(`sys_improvement_requests`)의 행까지 옮겼으므로 창구는 이제 하나다.
   *
   * ## 이름은 보내지 않는다 (사용자 지시 2026-09-02)
   *
   * 요청자는 익명이다. 대화로 낸 것만 이름이 실리면 <b>같은 목록에 두 종류의 글</b>이 생긴다 —
   * 화면에서 낸 사람은 가려지고 여기로 낸 사람은 안 가려지는데, 낸 사람은 그 차이를 모른다.
   */
  create_improvement_request: {
    kind: 'create_improvement_request',
    confirmLabel: '네, 등록해 주세요',
    invalidates: [queryKeys.requestBoardPosts],
    submit: async (p) => {
      await apiFetch('/v1/request-posts', {
        method: 'POST',
        body: { requesterName: null, requestContent: p.content },
      });
      return {
        message: '개선요청을 등록했습니다. 처리 상태는 개선요청 화면에서 보실 수 있습니다.',
        receipt: ['[실행됨] 개선요청 등록', text(p, 'content')].filter(Boolean).join(' · '),
        links: [{ label: '개선요청 열기', href: '/requests' }],
      };
    },
  },

  /**
   * 소모품 불출 신청 (2026-09-01).
   *
   * `POST /v1/supply-requisitions` 는 `asset_supplies` CREATE 를 요구하고
   * `submit(currentUser, request)` 로 세션 사용자 이름으로만 신청한다. 승인·불출은
   * `asset_supplies_admin` EDIT 이라 열지 않았다.
   *
   * <p><b>금액을 말하지 않는다.</b> DTO 주석 그대로 "금액은 서버가 품목 단가 × 수량으로
   * 산정한다(클라이언트 값 불신)" — 숫자를 적으면 사용자는 그 금액을 승인했다고 생각하는데
   * 실제 금액은 서버가 따로 낸다(에이전트 `tools/asset.py` 도 같은 이유로 적지 않는다).
   */
  create_supply_request: {
    kind: 'create_supply_request',
    confirmLabel: '네, 신청해 주세요',
    submit: async (p) => {
      await apiFetch('/v1/supply-requisitions', {
        method: 'POST',
        body: { supplyId: p.supplyId, quantity: p.quantity, reason: p.reason ?? null },
      });
      return {
        message: '소모품 불출을 신청했습니다. 진행 상태는 소모품 불출 화면에서 보실 수 있습니다.',
        receipt: ['[실행됨] 소모품 불출 신청', text(p, 'itemName'),
          `${p.quantity}${text(p, 'unit') ?? ''}`].filter(Boolean).join(' · '),
        links: [{ label: '소모품 불출 열기', href: '/assets/supplies' }],
      };
    },
  },

  /**
   * 회의실·차량 예약 (2026-09-01).
   *
   * ## 시각은 <b>여기서</b> 만든다
   *
   * backend 는 `Instant`(UTC)를 받는데 사용자가 말하는 것은 그 사람의 시간대다. 에이전트가
   * 변환하면 서버 시간대를 가정하게 되고, 그 가정이 틀리면 <b>두 시간 어긋난 예약</b>이 잡힌다.
   * 그래서 에이전트는 날짜·시각을 문자열로 넘기고, 브라우저가 자기 시간대로 바꾼다 —
   * 예약하는 사람이 앉아 있는 그 시간대다.
   *
   * ## 겹침은 서버가 막는다
   *
   * `reserve()` 가 `requireFree(...)` 로 판정한다. 대화가 미리 확인해 봐야 승낙까지의 사이에
   * 남이 잡을 수 있어, 판정을 두 곳에 두면 어긋난 쪽이 거짓말을 한다. 실패하면 서버가 준 사유가
   * 그대로 대화창에 뜬다({@link AgentChatProvider#runAction}).
   */
  create_asset_reservation: {
    kind: 'create_asset_reservation',
    confirmLabel: '네, 예약해 주세요',
    submit: async (p) => {
      // 로컬 시각 → ISO. `new Date('2026-09-02T14:00:00')` 는 **브라우저 시간대**로 읽힌다.
      const iso = (time: unknown) => new Date(`${String(p.date)}T${String(time)}:00`).toISOString();
      const created = await apiFetch<{ status?: string }>('/v1/asset-reservations', {
        method: 'POST',
        body: {
          assetId: p.assetId,
          startAt: iso(p.startTime),
          endAt: iso(p.endTime),
          purpose: p.purpose ?? null,
          destination: p.destination ?? null,
          passengerCount: p.passengerCount ?? null,
        },
      });
      // 확정인지 대기인지는 **서버 응답으로만** 안다. 화면이 자산별 규칙을 따로 알면
      // 승인 정책이 바뀔 때 두 곳이 어긋난다.
      const pending = created.status === 'REQUESTED';
      return {
        message: pending
          ? '예약을 신청했습니다. 담당자 승인 뒤에 확정됩니다.'
          : '예약했습니다.',
        receipt: ['[실행됨] 예약', text(p, 'assetName'),
          `${text(p, 'date')} ${text(p, 'startTime')}~${text(p, 'endTime')}`,
          pending ? '승인 대기' : '확정'].filter(Boolean).join(' · '),
        // 예약 화면은 종류별로 갈려 있다(`screens.tsx`) — 방금 잡은 그 화면으로 보낸다.
        links: [p.assetType === 'VEHICLE'
          ? { label: '차량 예약 열기', href: '/assets/reservations/vehicles' }
          : { label: '회의실 예약 열기', href: '/assets/reservations/rooms' }],
      };
    },
  },

  /**
   * 현장 작업일보 작성 — <b>헤더까지만</b> (사용자 결정 2026-09-01).
   *
   * `POST /pmis/daily-logs` 는 `site_pmis` CREATE 를 요구하고 <b>서비스 첫 줄이
   * `siteAccess.require(siteId)`</b> 다 — 계층2가 backend 에 이미 있어 남의 현장에는 못 쓴다.
   * 현장 업무 등록 때는 그 게이트를 우리가 먼저 걸어야 했던 것과 갈리는 지점이다.
   *
   * <p>공정별 실적(`items`)은 보내지 않는다. WBS 항목을 골라야 하는데, 공정이 수십 개인 현장에서
   * 모델이 엉뚱한 공정을 고르면 <b>그 값이 현장 정보에 발주처 보고 수치로 그려진다.</b>
   * 저장한 뒤 일보 화면에서 이어 적도록 링크를 준다.
   */
  create_daily_log: {
    kind: 'create_daily_log',
    confirmLabel: '네, 저장해 주세요',
    submit: async (p) => {
      await apiFetch('/v1/pmis/daily-logs', {
        method: 'POST',
        body: {
          siteId: p.siteId,
          logDate: p.logDate,
          weather: p.weather ?? null,
          manpowerCount: p.manpowerCount ?? null,
          todayWork: p.todayWork,
          tomorrowPlan: p.tomorrowPlan ?? null,
          items: [],
        },
      });
      return {
        message: '작업일보를 저장했습니다. 공정별 실적은 일보 화면에서 이어 적으실 수 있습니다.',
        receipt: ['[실행됨] 작업일보 작성', text(p, 'siteName'), text(p, 'logDate'),
          text(p, 'todayWork')].filter(Boolean).join(' · '),
        links: [{ label: '작업일보 열기', href: '/sites/pmis' }],
      };
    },
  },

  /**
   * 주간회의 <b>본사(부서)</b> 업무 등록 (2026-09-01). {@link create_site_task} 의 쌍이다.
   *
   * <p>현장 업무는 계층2(`SiteScopeGuard`)를 먼저 걸어야 했지만 여기는 가를 축이 없다 —
   * 본사 업무는 부서별 표 하나이고, 그 표를 볼 수 있는 사람이 곧 쓸 수 있는 사람이다(화면도 같다).
   * `POST /v1/hq-tasks` 는 `meetings_hq` CREATE 를 요구한다.
   */
  create_hq_task: {
    kind: 'create_hq_task',
    confirmLabel: '네, 등록해 주세요',
    submit: async (p) => {
      await apiFetch('/v1/hq-tasks', {
        method: 'POST',
        body: {
          weekId: p.weekId,
          departmentId: p.departmentId,
          taskType: p.taskType,
          content: p.content,
          scheduleDate: null,
        },
      });
      return {
        message: '주간회의 본사 업무에 등록했습니다.',
        receipt: ['[실행됨] 주간회의 본사 업무 등록',
          text(p, 'weekLabel'), text(p, 'departmentName'), text(p, 'taskTypeLabel'), text(p, 'content'),
        ].filter(Boolean).join(' · '),
        links: [{ label: '주간업무 열기', href: '/meetings/weekly' }],
      };
    },
  },

  /**
   * <b>쉬운 모드 켜기/끄기</b> (사용자 지시 2026-09-23).
   *
   * ## 탭 줄의 버튼을 대신한다
   *
   * 현장 조회 탭 줄 맨 오른쪽에 [쉬운 모드] 토글이 있었고, 그것을 없앴다(`SitePmisPage`).
   * 기능은 그대로다 — 큰 글씨·「다음 할 일」 띠·검측 4단계. 바뀐 것은 <b>켜는 손</b>뿐이고,
   * 그 손이 여기다: 「글씨 좀 크게 해줘」·「쉬운 모드 꺼줘」.
   *
   * ## 인가를 조사할 것이 없다
   *
   * <b>서버를 부르지 않는다.</b> 쉬운 모드는 계정이 아니라 <b>이 기기</b>의 값이고
   * (`bomi.easyMode` · localStorage), 그렇게 둔 이유가 「같은 사람이라도 기기마다 답이 다르다」
   * 이다(`useEasyMode` 머리말). 남의 화면을 가리키는 값 자체가 없으므로 계층2가 성립하지 않는다 —
   * `set_greeting`·`create_todo` 가 「경로에도 본문에도 사람이 없다」로 열렸던 것보다 한 칸 더 안쪽이다.
   *
   * ## 캐시를 내릴 것도 없다
   *
   * 저장이 `CHANGE_EVENT` 를 쏘고 {@code useEasyMode} 를 쓰는 화면이 <b>그 자리에서</b> 다시
   * 그린다. 조회 캐시를 타지 않는 값이라 `invalidates` 가 빈 채로 맞다.
   */
  set_easy_mode: {
    kind: 'set_easy_mode',
    // 켜기인지 끄기인지는 **에이전트의 문장**이 말한다 — `set_greeting` 과 같은 규칙이다.
    confirmLabel: '네, 바꿔 주세요',
    submit: async (p) => {
      const on = !!p.enabled;
      // 이미 그 상태면 <b>바꿨다고 말하지 않는다.</b> 켜져 있는데 "켰습니다" 를 들으면 사람은
      // 자기가 뭘 잘못 봤나 싶어 한 번 더 시킨다.
      if (easyModeOn() === on) {
        return {
          message: on
            ? '쉬운 모드는 이미 켜져 있습니다.'
            : '쉬운 모드는 이미 꺼져 있습니다.',
          receipt: `[실행됨] 쉬운 모드 — 이미 ${on ? '켜짐' : '꺼짐'}`,
        };
      }
      setEasyMode(on);
      return {
        message: on
          ? '쉬운 모드를 켰습니다. 글씨와 버튼이 커지고, 지금 무엇을 눌러야 하는지 화면 위에 한 줄로 나옵니다. 이 기기에서만 적용됩니다.'
          : '쉬운 모드를 껐습니다. 글씨와 버튼이 원래 크기로 돌아갑니다.',
        receipt: `[실행됨] 쉬운 모드 ${on ? '켜기' : '끄기'}`,
      };
    },
  },

  /**
   * 공정 진척 저장 (DOC-109 §4 · 인가 조사 DOC-20 §1.12 2026-10-06).
   *
   * 두 겹이 다 있어 열었다 — `MenuGuard(site_pmis…, EDIT)` 와 `siteAccess.requireOwning(행의 siteId)`.
   * 현장 전용 계정이 남의 현장 줄을 고치면 403 이고, 그 사유가 영수증 자리에 그대로 뜬다.
   *
   * ## 「현장 공정율 60%」는 이 kind 로 오지 않는다
   *
   * 서버가 받는 것은 **(공정, 날짜) 스냅샷**이고 현장 숫자는 거기서 롤업된다. 그래서 에이전트가
   * `wbsItemId` 와 `recordDate` 를 정하지 못하면 **이 초안을 내리지 않고 되묻는다**(`choice`).
   * 카탈로그가 그것을 강제할 수는 없으므로, 값이 비면 여기서 막는다 — 반쯤 든 값으로 서버를
   * 부르지 않는 것은 `attachedDocs` 와 같은 규율이다.
   *
   * ## 업서트다
   *
   * 같은 (공정, 날짜)면 **덮는다**. 쌓이지 않는다 — 확인 게이트가 필요한 이유이고, 문장이
   * 「58% → 65%」처럼 **바뀌기 전 값까지** 말해야 하는 이유다.
   */
  save_wbs_progress: {
    kind: 'save_wbs_progress',
    confirmLabel: '네, 저장해 주세요',
    submit: async (p) => {
      const wbsItemId = text(p, 'wbsItemId');
      const recordDate = text(p, 'recordDate');
      if (!wbsItemId || !recordDate) {
        // 어느 공정의 며칠인지가 없으면 저장이 성립하지 않는다. 서버에 400 을 받으러 가지 않는다.
        return {
          message: '어느 공정의 며칠 실적인지가 정해지지 않았습니다. 공정과 날짜를 알려 주세요.',
          receipt: '[실행 안 함] 공정 실적 저장 — 공정·날짜 미정',
        };
      }

      await apiFetch(`/v1/pmis/wbs/${wbsItemId}/progress`, {
        method: 'PUT',
        body: {
          recordDate,
          // 적지 않은 쪽은 null 로 보낸다 — 「실적만 올린다」가 예정율을 0 으로 덮으면 안 된다.
          plannedRate: p.plannedRate ?? null,
          actualRate: p.actualRate ?? null,
          note: p.note ?? null,
        },
      });

      return {
        message: '공정 실적을 저장했습니다. 현장 공정율은 공정별 실적에서 다시 합산됩니다.',
        receipt: [
          '[실행됨] 공정 실적 저장',
          text(p, 'siteName'), text(p, 'wbsName'), recordDate,
          text(p, 'actualRate') === null ? null : `실적 ${text(p, 'actualRate')}%`,
          text(p, 'plannedRate') === null ? null : `예정 ${text(p, 'plannedRate')}%`,
        ].filter(Boolean).join(' · '),
        links: [{ label: '공정표 열기', href: '/sites/pmis' }],
      };
    },
    // 공정표·공정율은 대화 밖 화면이 그린다. 내리지 않으면 "저장했습니다" 를 보고 가서 옛 값을 만난다.
    invalidates: [['pmis']],
  },

  /**
   * 현장 직원의 <b>담당 한 칸</b>을 바꾼다 — 「김낙환 부장 현장소장으로 옮겨줘」 (사용자 지시 2026-10-28 · DOC-20 §1.12).
   *
   * 전술판 자리 관리 창과 같은 `PUT /v1/assignments/{id}/duty` 다. 계층1은 `hr_placement` EDIT,
   * 그 위에 대표·본부장급·인사배치 담당만 통과하는 문(`PlacementAuthority.requireEditor`)이 있다.
   * 회사 전체의 배치표라 현장 범위(계층2)로 가르는 자리가 아니다.
   *
   * <p>배치 줄 id 는 에이전트가 모른다(위임으로 `/assignments` 를 읽으면 본인 것으로 바뀐다). 그래서
   * 사람과 현장 이름으로 받아 <b>여기서</b> 세션으로 찾는다. 못 찾으면 부르지 않는다.
   */
  set_site_duty: {
    kind: 'set_site_duty',
    confirmLabel: '네, 바꿔 주세요',
    submit: async (p) => {
      const personnelId = text(p, 'personnelId');
      const siteName = text(p, 'siteName');
      const duty = text(p, 'duty');
      const who = text(p, 'personName') ?? '';
      if (!personnelId || !siteName || !duty) {
        return {
          message: '누구의 어느 현장 담당인지 정해지지 않았습니다. 사람과 현장을 다시 말씀해 주세요.',
          receipt: '[실행 안 함] 현장 담당 변경 — 대상 미정',
        };
      }

      const rows = await apiFetch<{ id: string; assignmentType: string; assignmentName: string;
        roleTitle: string | null; endDate: string | null }[]>(
        `/v1/assignments?personnelId=${encodeURIComponent(personnelId)}`);
      const today = new Date().toISOString().slice(0, 10);
      const row = rows.find((r) => r.assignmentType === 'SITE' && r.assignmentName.trim() === siteName.trim()
        && (!r.endDate || r.endDate >= today));
      if (!row) {
        return {
          message: `${who}님의 ${siteName} 배치를 찾지 못했습니다. 인력배치표에서 확인해 주세요.`,
          receipt: `[실행 안 함] 현장 담당 변경 — ${siteName} 배치 없음`,
          links: [{ label: '인력배치표 열기', href: '/hr/placement' }],
        };
      }
      if ((row.roleTitle ?? '').trim() === duty) {
        return {
          message: `${who}님은 이미 ${siteName}의 ${duty}입니다.`,
          receipt: `[실행됨] 현장 담당 변경 — 이미 ${duty}`,
        };
      }

      await apiFetch(`/v1/assignments/${row.id}/duty`, { method: 'PUT', body: { duty, recordOrder: true } });
      return {
        message: `${who}님을 ${siteName}의 ${duty}(으)로 바꿨습니다. 발령 대장에도 남겼습니다.`,
        receipt: ['[실행됨] 현장 담당 변경', siteName, who,
          [text(p, 'fromDuty'), duty].filter(Boolean).join(' → ')].filter(Boolean).join(' · '),
        links: [{ label: '인력배치표 열기', href: '/hr/placement' }],
      };
    },
    // 배치표·직원찾기·발령 대장·현장 연락처가 같은 줄을 그린다.
    invalidates: [queryKeys.personnelAssignments, queryKeys.directoryTree, queryKeys.placementOrders, ['pmis'], ['org-layout']],
  },

  // ⛔ 여기에 kind 를 더하기 전에 **그 경로가 DOC-20 §1.2 인가 전수조사 표에 있는지** 확인한다.
  //
  //    한때 `create_request`(→ `POST /v1/request-posts`)를 "일반 카드의 첫 사례" 로 미리 넣어
  //    두었다가 지웠다. 그 경로는 조사 표에 없었다 — 계층2가 있는지 아무도 확인하지 않은 채
  //    쓰기 통로만 깔아 둔 셈이고, 그것이 DOC-20 §3 이 "위험 1" 로 적은 바로 그 실수다.
  //    카탈로그는 "쓰기가 어떤 모양인가" 를 보여 주는 곳이지 **미리 열어 두는 곳이 아니다.**
  //
  //    순서는 언제나 이렇다:  인가 조사 → backend 확인/보강 → 에이전트 action → 이 카탈로그.
};

/** 모르는 kind 는 `null`. 호출부가 **조용히 넘기지 말고 드러내야** 한다. */
export function resolveAction(kind: string): ActionSpec | null {
  return ACTIONS[kind] ?? null;
}
